import { useQuery } from "@apollo/react-hooks";
import React from "react";
import styled from "styled-components";

import { PanelContext } from "../../contexts/PanelContext";
import {
  Get_Point_By_IdQuery,
  Get_Point_By_IdQueryVariables,
  NodeEvent,
  NodeHeritageSite,
  NodeSayings
} from "../../generated/graphql";
import { GET_POINT_QUERY } from "../../graphql/queries/get-point";
import AboutPanelContent from "./AboutPanelContent";
import CloseButton from "./CloseButton";
import { InnerWrapper } from "./InnerWrapper";
import PanelContentWrapper from "./PanelContentWrapper";
import SubmitStoryContent from "./SubmitStoryContent";
import UserSayingContent from "./UserSayingContent";
import { LIST_EVENTS_BY_SITE_ID } from "../../graphql/queries/list-events-by-site-id";
import {
  List_Events_By_Site_IdQuery,
  List_Events_By_Site_IdQueryVariables
} from "../../generated/graphql";
import { motion, AnimatePresence } from "framer-motion"


interface PanelProps { }

type NodeEntity = NodeHeritageSite & NodeEvent & NodeSayings;

const Panel: React.FC<PanelProps> = props => {
  const [panel] = React.useContext(PanelContext);
  const [panelReady, setPanelReady] = React.useState(false);
  const [panelData, setPanelData] = React.useState<NodeEntity>();
  const [associatedEvents, setAssociatedEvents] = React.useState<NodeEvent[]>();


  const { loading: isPanelLoading, refetch: getPointById } = useQuery<
    Get_Point_By_IdQuery,
    Get_Point_By_IdQueryVariables
  >(GET_POINT_QUERY, { skip: true });

  const { refetch: getAssociatedEvents } = useQuery<
    List_Events_By_Site_IdQuery,
    List_Events_By_Site_IdQueryVariables
  >(LIST_EVENTS_BY_SITE_ID, { skip: true });

  React.useEffect(() => {
    if (panel.open) {
      if (panel.data?.id && panel.data.id !== panelData?.entityId) {
        getPointById({
          id: panel.data.id
        })
          .then(result => {
            setPanelData(result.data.nodeById as NodeEntity);

            if (result.data.nodeById) {
              if (result.data.nodeById.__typename === "NodeHeritageSite") {
                getAssociatedEvents({
                  id: result.data.nodeById.entityId as string
                })
                  .then(associatedResult => {
                    setPanelReady(true);
                    if (associatedResult.data.nodeQuery?.entities) {
                      setAssociatedEvents(
                        associatedResult.data.nodeQuery?.entities as NodeEvent[]
                      );
                    }
                  })
                  .catch(error => {
                    console.error(error);
                  });
              } else {
                setPanelReady(true);
              }
            }
          })
          .catch(error => {
            console.error(error);
          });
      } else {
        setPanelReady(true);
      }
    } else {
      setPanelReady(false);
    }
  }, [panel, getPointById, panelData, getAssociatedEvents]);

  return (
    <AnimatePresence>
      {panelReady && (
        <PanelWrapper
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          transition={{ duration: 0.3 }}
          exit={{ x: "100%" }}
        >
          <InnerWrapper>
            <PanelHeader>
              <CloseButton />
            </PanelHeader>
          </InnerWrapper>
          {isPanelLoading && <p>Loading...</p>}
          {panel.data?.type === "poi" && panelData && (
            <>
              {panelData.__typename === "NodeSayings" ? (
                <UserSayingContent data={panelData} />
              ) : (
                  <PanelContentWrapper
                    type={panel.data.type}
                    data={panelData}
                    associatedEvents={associatedEvents}
                  />
                )}
            </>
          )}
          {panel.data?.type === "about" && <AboutPanelContent />}
          {panel.data?.type === "story-form" && <SubmitStoryContent />}
        </PanelWrapper>
      )}
    </AnimatePresence>
  );
};

const PanelWrapper = styled(motion.div as any)`
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  width: 100%;
  background: white;
  overflow: scroll;
  z-index: 80;
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: 2rem 0;
  margin-top: 90px;
  @media (max-width: 960px) {
    margin-top: 60px;
  }
`;

export default Panel;
